import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import emailjs from "@emailjs/browser";
import { CapacitorHttp } from "@capacitor/core";
import { Star, Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { feedbackSchema, type FeedbackFormData } from "@/schemas/feedbackSchema";
import { emailjsConfig, type EmailParams } from "@/config/emailjs";
import { useAuth } from "@/hooks/useAuth";
import { isNative } from "@/utils/platform";

export const FEEDBACK_EVENT = "bfs:open-feedback";

export const FeedbackDialog = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { rating: 0, message: "" },
  });

  const rating = form.watch("rating");

  useEffect(() => {
    const handleOpen = () => {
      form.reset({ rating: 0, message: "" });
      setOpen(true);
    };
    window.addEventListener(FEEDBACK_EVENT, handleOpen);
    return () => window.removeEventListener(FEEDBACK_EVENT, handleOpen);
  }, [form]);

  const onSubmit = async (data: FeedbackFormData) => {
    setIsSubmitting(true);
    try {
      const emailParams: EmailParams = {
        from_name: user?.nickname || user?.full_name || "BuyForS user",
        reply_to: user?.email || "no-reply@buyfors.com",
        subject: `[In-App Feedback] ${data.rating}/5`,
        message: data.message,
        submission_time: new Date().toLocaleString(),
      };

      if (isNative()) {
        // WebView CSP blocks the EmailJS SDK on native; send via native HTTP instead.
        const response = await CapacitorHttp.request({
          method: "POST",
          url: "https://api.emailjs.com/api/v1.0/email/send",
          headers: {
            "Content-Type": "application/json",
            origin: "https://buyfors.com",
          },
          data: {
            service_id: emailjsConfig.serviceId,
            template_id: emailjsConfig.templateIds.admin,
            user_id: emailjsConfig.publicKey,
            template_params: emailParams,
          },
        });
        if (response.status < 200 || response.status >= 300) {
          throw new Error(`EmailJS responded with ${response.status}`);
        }
      } else {
        emailjs.init(emailjsConfig.publicKey);
        await emailjs.send(
          emailjsConfig.serviceId,
          emailjsConfig.templateIds.admin,
          emailParams
        );
      }

      toast.success("Thanks for your feedback!", {
        description: "We read every message and use it to improve the app.",
      });
      setOpen(false);
    } catch {
      toast.error("Couldn't send feedback", {
        description: "Please try again in a moment.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user?.id) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Tell us what you love or what we can improve. Sent to our team with your account email.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="rating"
              render={() => (
                <FormItem>
                  <div className="flex justify-center gap-2" role="radiogroup" aria-label="Rating">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={rating === value}
                        aria-label={`${value} star${value > 1 ? "s" : ""}`}
                        onClick={() => form.setValue("rating", value, { shouldValidate: true })}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <Star
                          className={`h-8 w-8 ${
                            value <= rating
                              ? "fill-amber-400 text-amber-400"
                              : "text-muted-foreground/40"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                  <FormMessage className="text-center" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder="What's on your mind?"
                      rows={5}
                      className="resize-none"
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send feedback
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
